import "./App.css";
import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { loadSlim } from "@tsparticles/slim";
import { useTheme } from './components/common/ThemeContext';
import MetaTags from '../src/components/common/MetaTags';
import ErrorBoundary from '../src/components/common/ErrorBoundary';
import ReactGA from 'react-ga';
import ClimbingBoxLoader from "react-spinners/ClimbingBoxLoader";

// Lazy-load heavy components
const Home           = lazy(() => import("./components/home/index"));
const Mouse          = lazy(() => import("./components/mouse"));
const Particles      = lazy(() => import("@tsparticles/react").then(m => ({ default: m.default ?? m.Particles })));
const GoToTop        = lazy(() => import('./components/GoToTop'));
const DarkModeToggle = lazy(() => import('../src/components/common/DarkModeToggle'));
const AnchorComponent = lazy(() => import('../src/components/common/Anchor'));

const App = () => {
    const { darkMode } = useTheme();
    const [loading, setLoading] = useState(true);

    // Single 1-second splash
    useEffect(() => {
        const t = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(t);
    }, []);

    // GA init once
    useEffect(() => {
        ReactGA.initialize('UA-249953473-1');
        ReactGA.pageview('/');
    }, []);

    const [numbers, setNumbers] = useState(() =>
        parseInt((window.innerHeight - 70) / 30)
    );

    useEffect(() => {
        const handleResize = () =>
            setNumbers(parseInt((window.innerHeight - 70) / 30));
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const particlesInit = useCallback(async engine => {
        await loadSlim(engine);
    }, []);

    // Stable particles options — memoised so Particles never re-mounts on re-render
    const particlesOptions = React.useMemo(() => ({
        fpsLimit: 60,
        interactivity: {
            events: {
                onClick: { enable: false },
                onHover: { enable: true, mode: "repulse" },
                resize: true,
            },
            modes: {
                repulse: { distance: 150, duration: 0.4 },
            },
        },
        particles: {
            color: { value: darkMode ? "#ffffff" : "#000000" },
            links: {
                color: "#951D25",
                distance: 150,
                enable: true,
                opacity: 0.25,
                width: 1,
            },
            collisions: { enable: false },   // cheaper
            move: {
                directions: "none",
                enable: true,
                outModes: { default: "bounce" },
                random: false,
                speed: 1.2,
                straight: false,
            },
            number: {
                density: { enable: true, area: 900 },
                value: 50,                   // fewer particles = faster
            },
            opacity: { value: 0.45 },
            shape: { type: "circle" },
            size: { value: { min: 1, max: 4 } },
        },
        detectRetina: true,
    }), [darkMode]);

    return (
        <ErrorBoundary>
            <div className="App">
                <MetaTags />
                {loading ? (
                    <div className="load">
                        <ClimbingBoxLoader
                            color={"#13be21"}
                            loading={loading}
                            size={30}
                            aria-label="Loading..."
                            data-testid="loader"
                        />
                    </div>
                ) : (
                    <Suspense fallback={null}>
                        <div className="app-container">
                            <AnchorComponent number={numbers} />
                            <DarkModeToggle />
                            <Particles
                                id="tsparticles"
                                init={particlesInit}
                                options={particlesOptions}
                            />
                            <Mouse />
                            <Home />
                            <GoToTop />
                        </div>
                    </Suspense>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default App;
